import FacebookIcon from '@/components/globals/Icons/FilterIcons/Platforms/FacebookPlatformIcon.vue'
import FBGroupsIcon from '@/components/globals/Icons/FilterIcons/Platforms/FBGroupsPlatformIcon.vue'
import GoogleIcon from '@/components/globals/Icons/FilterIcons/Platforms/GooglePlatformIcon.vue'
import InstagramIcon from '@/components/globals/Icons/FilterIcons/Platforms/InstagramPlatformIcon.vue'
import LinkedInIcon from '@/components/globals/Icons/FilterIcons/Platforms/LinkedInPlatformIcon.vue'
import PinterestIcon from '@/components/globals/Icons/FilterIcons/Platforms/PinterestPlatformIcon.vue'
import SnapchatIcon from '@/components/globals/Icons/FilterIcons/Platforms/SnapchatPlatformIcon.vue'
import TikTokIcon from '@/components/globals/Icons/FilterIcons/Platforms/TikTokPlatformIcon.vue'
import XIcon from '@/components/globals/Icons/FilterIcons/Platforms/XPlatformIcon.vue'
import WhatsAppIcon from '@/components/globals/Icons/FilterIcons/Platforms/WhatsAppPlatformIcon.vue'
import YouTubeIcon from '@/components/globals/Icons/FilterIcons/Platforms/YouTubePlatformIcon.vue'
import OtherIcon from '@/components/globals/Icons/FilterIcons/Niches/OtherNicheIcon.vue'

// Based on the distinct 'network' values in the spyder.domain_socials table
const platformIconMap = new Map([
  ['facebook', FacebookIcon],
  ['facebook_group', FBGroupsIcon],
  ['google', GoogleIcon],
  ['instagram', InstagramIcon],
  ['linkedin', LinkedInIcon],
  ['pinterest', PinterestIcon],
  ['snapchat', SnapchatIcon],
  ['tiktok', TikTokIcon],
  ['twitter', XIcon],
  ['whatsapp', WhatsAppIcon],
  ['youtube', YouTubeIcon]
])
const platformNameMap = new Map([
  ['facebook', 'Facebook'],
  ['facebook_group', 'Facebook Groups'],
  ['google', 'Google'],
  ['instagram', 'Instagram'],
  ['linkedin', 'LinkedIn'],
  ['pinterest', 'Pinterest'],
  ['snapchat', 'Snapchat'],
  ['tiktok', 'TikTok'],
  ['twitter', 'X/Twitter'],
  ['whatsapp', 'WhatsApp'],
  ['youtube', 'YouTube']
])

const getSocialPlatformIcon = (platformKey) => {
  return platformIconMap.get(platformKey) || OtherIcon
}

const getSocialPlatformName = (platformKey) => {
  return platformNameMap.get(platformKey) || 'Other'
}

// Extracts the account handle from a social url, detecting the platform from the host.
// Returns null when the url is malformed, incompatible, or has no handle (e.g. Google, WhatsApp).
const extractSocialHandle = (url) => {
  if (!url || typeof url !== 'string') return null

  let parsed
  try {
    parsed = new URL(url.trim())
  } catch (e) {
    // Fall back to assuming a protocol-less url like 'instagram.com/nike'
    try {
      parsed = new URL(`https://${url.trim()}`)
    } catch (e2) {
      return null
    }
  }

  const host = parsed.hostname.replace(/^www\./, '').toLowerCase()
  const segments = parsed.pathname.split('/').filter(Boolean)

  // Normalize a raw path segment into a handle (decoded, no leading '@')
  const clean = (value) => {
    if (!value) return null
    const handle = decodeURIComponent(value).replace(/^@/, '').trim()
    return handle || null
  }

  // instagram.com/{handle}, pinterest.com/{handle} -> first path segment.
  // Instagram app deep links use a '/_u/{handle}' prefix, so skip it when present.
  if (host.endsWith('instagram.com') || host.includes('pinterest.')) {
    if (segments[0] && segments[0].toLowerCase() === '_u') return clean(segments[1])
    return clean(segments[0])
  }

  // twitter.com/{handle}, x.com/{handle} -> first segment, ignoring reserved paths
  if (host.endsWith('twitter.com') || host.endsWith('x.com')) {
    const reserved = ['i', 'home', 'search', 'hashtag', 'intent', 'share']
    if (!segments[0] || reserved.includes(segments[0].toLowerCase())) return null
    return clean(segments[0])
  }

  // tiktok.com/@{handle}
  if (host.endsWith('tiktok.com')) {
    return clean(segments.find(segment => segment.startsWith('@')) || segments[0])
  }

  // linkedin.com/company/{handle}, /in/{handle}, /school/{handle}, /pub/{handle}
  if (host.endsWith('linkedin.com')) {
    const idx = segments.findIndex(segment => ['company', 'in', 'school', 'pub'].includes(segment.toLowerCase()))
    return idx !== -1 ? clean(segments[idx + 1]) : null
  }

  // youtube.com/@{handle}, /c/{handle}, /user/{handle}, or legacy vanity youtube.com/{handle}.
  // /channel/{id} is an id (not a handle), and app routes like /watch are not handles.
  if (host.endsWith('youtube.com')) {
    const first = (segments[0] || '').toLowerCase()
    if (segments[0] && segments[0].startsWith('@')) return clean(segments[0])
    if (['c', 'user'].includes(first)) return clean(segments[1])
    const reserved = ['channel', 'watch', 'playlist', 'results', 'feed', 'shorts', 'hashtag', 'about', 'embed', 'live', 'gaming', 'premium']
    if (!segments[0] || reserved.includes(first)) return null
    return clean(segments[0])
  }

  // snapchat.com/add/{handle} or snapchat.com/{handle}
  if (host.endsWith('snapchat.com')) {
    const idx = segments.findIndex(segment => segment.toLowerCase() === 'add')
    return idx !== -1 ? clean(segments[idx + 1]) : clean(segments[0])
  }

  // facebook.com/{handle}, /groups/{id}, /pages/{name}/{id}. profile.php and bare numeric
  // ids (e.g. /100087634658721) are opaque ids, not handles, so they resolve to null.
  if (host.endsWith('facebook.com') || host.endsWith('fb.com')) {
    const groupIdx = segments.findIndex(segment => segment.toLowerCase() === 'groups')
    if (groupIdx !== -1) return clean(segments[groupIdx + 1])
    const first = (segments[0] || '').toLowerCase()
    if (first === 'pages') return clean(segments[1])
    if (first === 'profile.php') return null
    if (/^\d+$/.test(first)) return null
    return clean(segments[0])
  }

  // Google, WhatsApp (phone numbers), and unknown hosts have no meaningful handle
  return null
}

// Extracts the domain name from a social url, without the 'www.' prefix or the TLD extension
// (e.g. 'https://www.instagram.com/nike' -> 'instagram'). Returns null for missing/malformed urls.
const extractSocialDomain = (url) => {
  if (!url || typeof url !== 'string') return null

  let parsed
  try {
    parsed = new URL(url.trim())
  } catch (e) {
    // Fall back to assuming a protocol-less url like 'instagram.com/nike'
    try {
      parsed = new URL(`https://${url.trim()}`)
    } catch (e2) {
      return null
    }
  }

  const labels = parsed.hostname.replace(/^www\./, '').toLowerCase().split('.').filter(Boolean)
  if (!labels.length) return null
  // The registrable name is the label just before the TLD (e.g. [instagram, com] -> 'instagram')
  return labels.length >= 2 ? labels[labels.length - 2] : labels[0]
}

// Builds a case-insensitive key for duplicate detection: protocol and 'www.' are ignored, and
// twitter.com is treated as x.com. Flags the preferred x.com variant and whether the url carries
// meaningful casing (uppercase letters in the path/query) so cased duplicates can be prioritized.
const getSocialDedupInfo = (url) => {
  let parsed
  try {
    parsed = new URL(url && url.includes('://') ? url : `https://${url || ''}`)
  } catch (e) {
    return { key: (url || '').toLowerCase(), isXDomain: false, hasCasing: /[A-Z]/.test(url || '') }
  }
  const host = parsed.hostname.replace(/^www\./, '').toLowerCase()
  const isXDomain = host === 'x.com'
  const normalizedHost = host === 'twitter.com' ? 'x.com' : host
  const identifier = `${parsed.pathname.replace(/\/+$/, '')}${parsed.search}`
  return {
    key: `${normalizedHost}${identifier}`.toLowerCase(),
    isXDomain,
    hasCasing: /[A-Z]/.test(identifier)
  }
}

// Takes rows from the spyder.domain_socials table
const normalizeSocialsForDisplay = (socials) => {
  // Remove google network from the socials list (not viable for exposure)
  let filteredSocials = socials.filter(social => social.network !== 'google')
  // If there are multiple youtube socials, keep only the one that has a custom domain (no '/channel' in the url)
  // If there is no such domain, keep only the first one
  const youtubeSocials = filteredSocials.filter(social => social.network === 'youtube')
  if (youtubeSocials.length > 1) {
    const preferredYoutube = youtubeSocials.find(social => !(social.url || '').includes('/channel')) || youtubeSocials[0]
    filteredSocials = filteredSocials.filter(
      social => social.network !== 'youtube' || social === preferredYoutube
    )
  }

  // Filter out duplicate urls, ignoring protocol/www/case and treating twitter.com === x.com.
  // On a duplicate, prefer the x.com variant, then the one that carries meaningful casing;
  // otherwise keep the first occurrence.
  const socialsByKey = new Map()
  const dedupPriority = (info) => (info.isXDomain ? 2 : 0) + (info.hasCasing ? 1 : 0)
  filteredSocials.forEach(social => {
    const info = getSocialDedupInfo(social.url)
    const existing = socialsByKey.get(info.key)
    if (!existing || dedupPriority(info) > dedupPriority(existing)) {
      socialsByKey.set(info.key, { social, ...info })
    }
  })
  filteredSocials = [...socialsByKey.values()].map(entry => entry.social)

  // Identical follower counts within the same network strongly imply the same account under an
  // alias, so keep only the first social per (network + follower count). Skip unknown/zero counts,
  // which are common and not a reliable signal.
  const seenFollowerCounts = new Set()
  filteredSocials = filteredSocials.filter(social => {
    if (typeof social.followersCount !== 'number' || social.followersCount <= 0) return true
    const key = `${social.network}:${social.followersCount}`
    if (seenFollowerCounts.has(key)) return false
    seenFollowerCounts.add(key)
    return true
  })

  // Group same-network socials adjacently, preserving each network's first-appearance order
  const socialsByNetwork = new Map()
  filteredSocials.forEach(social => {
    if (!socialsByNetwork.has(social.network)) socialsByNetwork.set(social.network, [])
    socialsByNetwork.get(social.network).push(social)
  })
  filteredSocials = [...socialsByNetwork.values()].flat()

  const normalizedSocials = filteredSocials.map(social => ({
    network: social.network,
    name: getSocialPlatformName(social.network),
    icon: getSocialPlatformIcon(social.network),
    url: social.url,
    followers: social.followersCount,
    handle: extractSocialHandle(social.url)
  }))

  // A very short extracted handle (<= 4 chars) signals a bad url, so drop it. Socials with no
  // extracted handle are left untouched.
  return normalizedSocials.filter(social => social.handle === null || social.handle.length > 4)
}

export {
  getSocialPlatformIcon,
  getSocialPlatformName,
  extractSocialHandle,
  extractSocialDomain,
  normalizeSocialsForDisplay
}